import IFinishingOptionWithId from "../models/finishingOptions/IFinishingOptionWithId";
import ICalculatorConfiguration from "../models/ICalculatorConfiguration";
import { ICalculatorInput } from "../models/ICalculatorInput";
import { ICalculatorOutput } from "../models/ICalculatorOutput";
import IPrintingOption from "../models/IPrintingOption";
import IPaperTypeWithId from "../models/paperTypes/IPaperTypeWithId";
import IPricingMatrix from "../models/pricingMatrix/IPricingMatrix";
import IPricingMatrixWithId from "../models/pricingMatrix/IPricingMatrixWithId";

export default function CalculatePrice(
  details: ICalculatorInput,
  data: ICalculatorConfiguration,
  minimumFee: number,
): ICalculatorOutput {
  try {
    if (
      details.selectedPrintingOption &&
      details.selectedPaperType &&
      details.selectedPaperSize &&
      details.selectedMode
    ) {
      const calculatedPrice = internalCalculatePrice(details, data);

      if (calculatedPrice.hasPrice && calculatedPrice.grandTotal < minimumFee) {
        calculatedPrice.printingPriceDetails.push({
          description: "Minimum Price Surcharge",
          quantity: 1,
          totalPrice: minimumFee - calculatedPrice.grandTotal,
        });
        updateFinalPrice(calculatedPrice);
      }

      return calculatedPrice;
    }

    return {
      hasPrice: false,
      errorMessage: "Required information is missing",
      printingPriceDetails: [],
      grandTotal: 0,
    } as ICalculatorOutput;
  } catch (e) {
    return {
      hasPrice: false,
      errorMessage: "Unexpected error occurred while calculating price.",
      printingPriceDetails: [],
      grandTotal: 0,
    };
  }
}

function internalCalculatePrice(
  details: ICalculatorInput,
  data: ICalculatorConfiguration,
): ICalculatorOutput {
  const printingOption = data.pricingConfiguration.printingOptions.filter(
    (po) => po.code === details.selectedPrintingOption,
  )[0];
  let totalBlackPages = details.pagesBlack * details.numCopies;
  let totalColourPages = details.pagesColour * details.numCopies;

  if (printingOption.requireMultiplesOfFour && printingOption.supportsMixMode) {
    // For the case of mixed mode in printing options with multiples of four pages
    // we need to charge 2 pages for each colour page selected
    if (printingOption.hasCover && details.selectedCoverMode === "black") {
      totalBlackPages -= 2;
    } else if (
      printingOption.hasCover &&
      details.selectedCoverMode === "colour"
    ) {
      totalColourPages -= 2;
    }

    const totalPages = totalBlackPages + totalColourPages;

    const newColourPages = totalColourPages * 2;
    if (newColourPages <= totalPages) {
      totalBlackPages = totalBlackPages - totalColourPages;
      totalColourPages = newColourPages;
    } else {
      totalBlackPages = 0;
      totalColourPages = totalPages;
    }

    if (printingOption.hasCover && details.selectedCoverMode === "black") {
      totalBlackPages -= 2;
    } else if (
      printingOption.hasCover &&
      details.selectedCoverMode === "colour"
    ) {
      totalColourPages -= 2;
    }
  }

  if (totalBlackPages + totalColourPages < printingOption.minimumPagesPerJob) {
    return {
      hasPrice: false,
      errorMessage:
        printingOption.name +
        " requires a job with at least " +
        printingOption.minimumPagesPerJob +
        " pages.",
      printingPriceDetails: [],
      grandTotal: 0,
    } as ICalculatorOutput;
  }

  const result = {
    hasPrice: true,
    errorMessage: null,
    printingPriceDetails: [],
    grandTotal: 0,
  } as ICalculatorOutput;

  const matchingPricingMatrices: IPricingMatrixWithId[] =
    findByMatchingPricingMatrices(
      data,
      details,
      details.numCopies * (totalBlackPages + totalColourPages),
    );

  const selectedPaperType = data.pricingConfiguration.paperTypes.filter(
    (pt) => pt.guid === details.selectedPaperType,
  )[0];

  createPriceDetailBuckets(
    matchingPricingMatrices,
    details,
    result,
    selectedPaperType,
  );

  if (details.selectedCoverPaperType) {
    const updatedPageCount = updateNumberOfPages(
      details.selectedCoverMode,
      details.selectedMode,
      details.numCopies,
      totalBlackPages,
      totalColourPages,
    );
    totalBlackPages = updatedPageCount.updatedBlackPages;
    totalColourPages = updatedPageCount.updatedColourPages;

    calculateCoverPage(details, data, result, printingOption);
  }
  if (matchingPricingMatrices.length !== 0) {
    calculatePrintingPrice(
      details,
      matchingPricingMatrices,
      totalBlackPages,
      totalColourPages,
      selectedPaperType,
      result,
      printingOption,
    );

    let finishingOptionRate = {
      description: "",
      fixed: 0,
      perPaper: 0,
      discountFor: 0,
    };
    if (details.selectedFinishingOption !== "") {
      finishingOptionRate = findMatchingFinishingOptions(data, details);
    }
    result.printingPriceDetails.push({
      description:
        finishingOptionRate.description +
        " [Fixed] [" +
        finishingOptionRate.discountFor +
        "% Discount] @ " +
        finishingOptionRate.fixed.toFixed(3),
      quantity: details.numCopies,
      totalPrice: details.numCopies * finishingOptionRate.fixed,
    });
    const numPages = details.isDuplexSelected
      ? Math.ceil((details.pagesBlack + details.pagesColour) / 2)
      : details.pagesBlack + details.pagesColour;
    result.printingPriceDetails.push({
      description:
        finishingOptionRate.description +
        " [Per Paper] [" +
        finishingOptionRate.discountFor +
        "% Discount] @ " +
        finishingOptionRate.perPaper.toFixed(3),
      quantity: details.numCopies * numPages,
      totalPrice: details.numCopies * numPages * finishingOptionRate.perPaper,
    });

    updateFinalPrice(result);
  }
  return result;
}
function updateNumberOfPages(
  selectedCoverMode: string,
  selectedMode: string,
  numCopies: number,
  totalBlackPages: number,
  totalColourPages: number,
) {
  if (selectedCoverMode !== selectedMode) {
    if (selectedCoverMode === "colour") {
      totalBlackPages = totalBlackPages - 2 * numCopies;
    } else {
      totalColourPages = totalColourPages - 2 * numCopies;
    }
  } else {
    if (selectedCoverMode === "colour") {
      totalColourPages = totalColourPages - 2 * numCopies;
    } else {
      totalBlackPages = totalBlackPages - 2 * numCopies;
    }
  }
  return {
    updatedBlackPages: totalBlackPages,
    updatedColourPages: totalColourPages,
  };
}
function calculateCoverPage(
  details: ICalculatorInput,
  data: ICalculatorConfiguration,
  result: ICalculatorOutput,
  printingOption: IPrintingOption,
) {
  const coverDetails = {
    userType: details.userType,
    numCopies: details.numCopies,
    isDuplexSelected: true,
    pagesBlack: details.selectedCoverMode === "black" ? 2 : 0,
    pagesColour: details.selectedCoverMode === "colour" ? 2 : 0,
    selectedPaperType: details.selectedCoverPaperType,
    selectedCoverPaperType: "",
    selectedCoverMode: "",
    selectedFinishingOption: details.selectedFinishingOption,
    selectedMode: details.selectedCoverMode,
    selectedPaperSize: details.selectedPaperSize,
    selectedPrintingOption: details.selectedPrintingOption,
  };
  const selectedCoverPaperType = data.pricingConfiguration.paperTypes.filter(
    (pt) => pt.guid === details.selectedCoverPaperType,
  )[0];

  const matchingCoverPricingMatrices: IPricingMatrixWithId[] =
    findByMatchingPricingMatrices(data, coverDetails, 2 * details.numCopies);

  createPriceDetailBuckets(
    matchingCoverPricingMatrices,
    coverDetails,
    result,
    selectedCoverPaperType,
  );

  if (matchingCoverPricingMatrices.length !== 0) {
    calculatePrintingPrice(
      coverDetails,
      matchingCoverPricingMatrices,
      coverDetails.selectedMode === "black" ? 2 * details.numCopies : 0,
      coverDetails.selectedMode === "colour" ? 2 * details.numCopies : 0,
      selectedCoverPaperType,
      result,
      printingOption,
      true,
    );
  }
}

function findByMatchingPricingMatrices(
  calculatorConfiguration: ICalculatorConfiguration,
  calculatorInput: ICalculatorInput,
  totalPages: number,
): IPricingMatrixWithId[] {
  return calculatorConfiguration.pricingConfiguration.pricingMatrix.filter(
    (entry: IPricingMatrix) =>
      entry.selectedPrintingOption === calculatorInput.selectedPrintingOption &&
      entry.paperTypes.includes(calculatorInput.selectedPaperType) &&
      entry.selectedPaperSize === calculatorInput.selectedPaperSize &&
      entry.duplex === calculatorInput.isDuplexSelected &&
      entry.pageRangeLow <= totalPages,
  );
}

function findMatchingFinishingOptions(
  calculatorConfiguration: ICalculatorConfiguration,
  calculatorInput: ICalculatorInput,
): {
  description: string;
  fixed: number;
  perPaper: number;
  discountFor: number;
} {
  const finishingOption =
    calculatorConfiguration.pricingConfiguration.finishingOptions.filter(
      (entry) => entry.guid === calculatorInput.selectedFinishingOption,
    );

  if (finishingOption[0]) {
    return internalCalculateFinishingOption(
      calculatorInput,
      finishingOption[0],
    );
  } else {
    throw new Error("Incorrect parameters for findMatchingFinishingOptions");
  }
}

function createPriceDetailBuckets(
  matchingPricingMatrices: IPricingMatrixWithId[],
  details: ICalculatorInput,
  result: ICalculatorOutput,
  selectedPaperType: IPaperTypeWithId,
) {
  matchingPricingMatrices.forEach((pm) => {
    if (details.userType === "individual") {
      result.printingPriceDetails.push({
        description:
          "Black Pages [Individual] @ " + pm.blackIndividualRate.toFixed(3),
        quantity: 0,
        totalPrice: 0,
      });
      result.printingPriceDetails.push({
        description:
          "Colour Pages [Individual] @ " + pm.colourIndividualRate.toFixed(3),
        quantity: 0,
        totalPrice: 0,
      });
      result.printingPriceDetails.push({
        description:
          selectedPaperType.displayName +
          " [Individual] @ " +
          selectedPaperType.individualRate.toFixed(3),
        quantity: 0,
        totalPrice: 0,
      });
    } else if (details.userType === "school") {
      result.printingPriceDetails.push({
        description: "Black Pages [School] @ " + pm.blackSchoolRate.toFixed(3),
        quantity: 0,
        totalPrice: 0,
      });
      result.printingPriceDetails.push({
        description:
          "Colour Pages [School] @ " + pm.colourSchoolRate.toFixed(3),
        quantity: 0,
        totalPrice: 0,
      });
      result.printingPriceDetails.push({
        description:
          selectedPaperType.displayName +
          " [School] @ " +
          selectedPaperType.schoolRate.toFixed(3),
        quantity: 0,
        totalPrice: 0,
      });
    }
  });
}

function calculatePrintingPrice(
  details: ICalculatorInput,
  matchingPricingMatrices: IPricingMatrixWithId[],
  totalBlackPages: number,
  totalColourPages: number,
  selectedPaperType: IPaperTypeWithId,
  result: ICalculatorOutput,
  printingOption: IPrintingOption,
  bookletCoverMode = false,
) {
  const userType = details.userType === "individual" ? "Individual" : "School";
  for (let i = 1; i <= totalBlackPages; i++) {
    const pricingMatrixForPageArray = matchingPricingMatrices.filter(
      (pm) => pm.pageRangeLow <= i && pm.pageRangeHigh >= i,
    );
    if (pricingMatrixForPageArray.length < 1) {
      throw new Error("Pricing Matrix not identified for black page " + i);
    }
    const pricingMatrixForPage = pricingMatrixForPageArray[0];
    let rate = 0;
    if (details.userType === "individual") {
      rate = pricingMatrixForPage.blackIndividualRate;
    } else if (details.userType === "school") {
      rate = pricingMatrixForPage.blackSchoolRate;
    }
    const description = "Black Pages [" + userType + "] @ " + rate.toFixed(3);
    const priceDetails = result.printingPriceDetails.filter(
      (d) => d.description === description,
    )[0];
    priceDetails.totalPrice += rate;
    priceDetails.quantity++;
  }
  for (let i = 1; i <= totalColourPages; i++) {
    const pricingMatrixForPageArray = matchingPricingMatrices.filter(
      (pm) => pm.pageRangeLow <= i && pm.pageRangeHigh >= i,
    );

    if (pricingMatrixForPageArray.length < 1) {
      throw new Error("Pricing Matrix not identified for black page " + i);
    }
    const pricingMatrixForPage = pricingMatrixForPageArray[0];
    let rate = 0;
    if (details.userType === "individual") {
      rate = pricingMatrixForPage.colourIndividualRate;
    } else if (details.userType === "school") {
      rate = pricingMatrixForPage.colourSchoolRate;
    }
    const description = "Colour Pages [" + userType + "] @ " + rate.toFixed(3);
    const priceDetails = result.printingPriceDetails.filter(
      (d) => d.description === description,
    )[0];
    priceDetails.totalPrice += rate;
    priceDetails.quantity++;
  }

  calculatePaperPrice(
    details,
    selectedPaperType,
    totalBlackPages,
    totalColourPages,
    result,
    printingOption,
    bookletCoverMode,
  );
}

function updateFinalPrice(result: ICalculatorOutput) {
  result.printingPriceDetails = result.printingPriceDetails
    .filter((d) => d.totalPrice !== 0)
    .sort((a, b) => (a.description < b.description ? 1 : -1));

  let printingTotal = 0;
  result.printingPriceDetails.forEach((pd) => {
    pd.totalPrice = Math.round(pd.totalPrice * 100) / 100;
    printingTotal += pd.totalPrice;
  });
  result.grandTotal = Math.round(printingTotal * 100) / 100;
}
function calculatePaperPrice(
  details: ICalculatorInput,
  selectedPaperType: IPaperTypeWithId,
  totalBlackPages: number,
  totalColourPages: number,
  result: ICalculatorOutput,
  printingOption: IPrintingOption,
  bookletCoverMode = false,
) {
  let paperDivider = 1;
  if (details.isDuplexSelected || bookletCoverMode) {
    paperDivider = 2;
  }
  if (printingOption.requireMultiplesOfFour && !bookletCoverMode) {
    paperDivider = 4;
  }
  const totalPages = Math.ceil(
    (totalBlackPages + totalColourPages) / paperDivider,
  );

  if (details.userType === "individual") {
    const paperTypeDescription =
      selectedPaperType.displayName +
      " [Individual] @ " +
      selectedPaperType.individualRate.toFixed(3);
    const priceDetails = result.printingPriceDetails.filter(
      (d) => d.description === paperTypeDescription,
    )[0];
    priceDetails.totalPrice += totalPages * selectedPaperType.individualRate;
    priceDetails.quantity += totalPages;
  } else if (details.userType === "school") {
    const paperTypeDescription =
      selectedPaperType.displayName +
      " [School] @ " +
      selectedPaperType.schoolRate.toFixed(3);
    const priceDetails = result.printingPriceDetails.filter(
      (d) => d.description === paperTypeDescription,
    )[0];
    priceDetails.totalPrice += totalPages * selectedPaperType.individualRate;
    priceDetails.quantity += totalPages;
  }
}
function internalCalculateFinishingOption(
  calculatorInput: ICalculatorInput,
  finishingOption: IFinishingOptionWithId,
): {
  description: string;
  fixed: number;
  perPaper: number;
  discountFor: number;
} {
  const numberOfPages = calculatorInput.isDuplexSelected
    ? Math.ceil((calculatorInput.pagesBlack + calculatorInput.pagesColour) / 2)
    : calculatorInput.pagesBlack + calculatorInput.pagesColour;
  const appliesTo = finishingOption.appliesTo.filter(
    (entryAT) =>
      entryAT.paperTypes.includes(calculatorInput.selectedPaperType) &&
      entryAT.selectedPaperSize === calculatorInput.selectedPaperSize &&
      entryAT.userType === calculatorInput.userType &&
      entryAT.pageRangeHigh >= numberOfPages &&
      entryAT.pageRangeLow <= numberOfPages,
  );

  if (appliesTo.length > 0) {
    const discountForList = finishingOption.discountFor.filter(
      (df) => df.copies <= calculatorInput.numCopies,
    );
    let highestRate = 0;
    if (discountForList.length > 0) {
      highestRate = Math.max(...discountForList.map((o) => o.discountrate));
    }

    return {
      description: finishingOption.displayName,
      fixed: ((100 - highestRate) / 100) * appliesTo[0].fixedFeeRate,
      perPaper: ((100 - highestRate) / 100) * appliesTo[0].pricePerPaperRate,
      discountFor: highestRate,
    };
  } else {
    return { description: "", fixed: 0, perPaper: 0, discountFor: 0 };
  }
}
